// client/src/pages/Game/Battlefield/components/CardPreview.jsx

import React from 'react';
import { formatEffect, getCardEffectDescription } from '../../../../utils/effectFormatter';

/**
 * Composant pour afficher la prévisualisation détaillée d'une carte
 * @param {Object} props Les propriétés du composant
 * @param {Object} props.card Carte à prévisualiser
 * @param {Function} props.getCardElement Fonction pour obtenir l'élément d'une carte
 * @returns {JSX.Element|null} Le composant CardPreview ou null si aucune carte n'est sélectionnée
 */
const CardPreview = ({
  card,
  getCardElement
}) => {
  if (!card) return null;
  
  // Fonction utilitaire pour sécuriser l'affichage des valeurs
  const safeDisplay = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number') return value;
    if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
    if (Array.isArray(value)) return '[Liste]';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };
  
  // Obtenir la couleur selon l'élément
  const getElementColor = (element) => {
    if (!element) return '#FFFFFF';
    
    const elementColors = {
      'FIRE': '#FF5A5A',
      'WATER': '#5ACDFF',
      'EARTH': '#8BC34A',
      'AIR': '#E0E0FF',
      'LIGHT': '#FFEB3B',
      'DARK': '#9C27B0',
      'RAINBOW': 'linear-gradient(90deg, #FF5A5A, #FFEB3B, #8BC34A, #5ACDFF, #9C27B0)'
    };
    
    return elementColors[element] || '#FFFFFF';
  };
  
  // Obtenir la couleur selon la rareté
  const getRarityColor = (rarity) => {
    if (!rarity) return '#FFFFFF';
    
    const rarityColors = {
      'COMMON': '#B0BEC5',
      'RARE': '#42A5F5',
      'SUPER_RARE': '#AB47BC',
      'EPIC': '#AB47BC',
      'SUPER_SUPER_RARE': '#FF6D00',
      'LEGENDARY': '#FFD700'
    };
    
    return rarityColors[rarity] || '#FFFFFF';
  };
  
  // Obtenir l'icône selon la vitesse du sort
  const getSpellSpeedIcon = (spellSpeed) => {
    if (!spellSpeed) return '';
    
    switch (spellSpeed) {
      case 'FAST':
        return '⚡'; // Éclair
      case 'SLOW':
        return '🕒'; // Horloge
      case 'FOCUS':
        return '🔮'; // Cristal
      case 'INSTANT':
        return '✨'; // Étincelles
      default:
        return '';
    }
  };
  
  // Style pour la prévisualisation de la carte
  const previewStyle = {
    backgroundColor: 'transparent', // Transparent car l'arrière-plan est géré par le conteneur parent
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '100%',
    maxHeight: '100%',
    overflow: 'auto'
  };
  
  // Style pour la carte agrandie
  const cardImageStyle = {
    width: '220px',
    height: '320px',
    marginBottom: '15px',
    objectFit: 'cover',
    borderRadius: '10px',
    boxShadow: `0 0 15px ${getElementColor(card.element)}`
  };
  
  // Obtenir l'info de vitesse du sort (si applicable)
  const getSpellSpeedInfo = () => {
    if (!card.spellSpeed) return null;
    
    return (
      <div style={{ 
        marginBottom: '10px', 
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        padding: '5px 10px',
        borderRadius: '5px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px'
      }}>
        <span>{getSpellSpeedIcon(card.spellSpeed)}</span>
        <span>Vitesse: {safeDisplay(card.spellSpeed)}</span>
      </div>
    );
  };
  
  // Afficher les effets de la carte
  const renderEffects = () => {
    if (!card.effects || !Array.isArray(card.effects) || card.effects.length === 0) {
      // Si pas d'effets structurés mais une description texte
      const desc = getCardEffectDescription(card);
      if (desc && desc !== 'Aucun effet') {
        return (
          <div style={{ marginTop: '15px', width: '100%' }}>
            <h4 style={{ 
              borderBottom: `1px solid ${getElementColor(card.element)}`, 
              paddingBottom: '5px',
              color: getElementColor(card.element)
            }}>
              Effet
            </h4>
            <p style={{ textAlign: 'left', lineHeight: '1.5', fontSize: '14px' }}>{desc}</p>
          </div>
        );
      }
      return null;
    }
    
    return (
      <div style={{ marginTop: '15px', width: '100%' }}>
        <h4 style={{ 
          borderBottom: `1px solid ${getElementColor(card.element)}`, 
          paddingBottom: '5px',
          color: getElementColor(card.element)
        }}>
          Effets
        </h4>
        <ul style={{ textAlign: 'left', padding: '0 0 0 20px' }}>
          {card.effects.map((effect, index) => {
            // Vérifier que l'effet est un objet valide
            if (!effect || typeof effect !== 'object') {
              return <li key={index}>Effet invalide</li>;
            }
            
            const formatted = formatEffect(effect);
            
            return (
              <li key={index} style={{ 
                marginBottom: '10px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                padding: '8px',
                borderRadius: '5px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{ display: 'flex', gap: '5px', marginBottom: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {effect.type && (
                    <span style={{ 
                      fontWeight: 'bold',
                      color: getElementColor(card.element),
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      padding: '2px 5px',
                      borderRadius: '3px',
                      fontSize: '12px'
                    }}>
                      {safeDisplay(effect.type)}
                    </span>
                  )}
                  
                  {effect.trigger && (
                    <span style={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      padding: '2px 5px',
                      borderRadius: '3px',
                      fontSize: '12px'
                    }}>
                      Déclencheur: {safeDisplay(effect.trigger)}
                    </span>
                  )}
                  
                  {effect.condition && (
                    <span style={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      padding: '2px 5px',
                      borderRadius: '3px',
                      fontSize: '12px'
                    }}>
                      Condition: {safeDisplay(effect.condition)}
                    </span>
                  )}
                  
                  {effect.target && (
                    <span style={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      padding: '2px 5px',
                      borderRadius: '3px',
                      fontSize: '12px'
                    }}>
                      Cible: {safeDisplay(effect.target)}
                    </span>
                  )}
                </div>
                
                <div style={{ marginTop: '5px', lineHeight: '1.5', fontSize: '14px' }}>
                  {formatted}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };
  
  // Afficher la citation de la carte
  const renderFlavorText = () => {
    if (!card.flavourText && !card.flavorText && !card.description) return null;
    
    const flavorText = safeDisplay(card.flavourText || card.flavorText || card.description);
    
    return (
      <div style={{ 
        marginTop: '15px', 
        padding: '10px', 
        fontStyle: 'italic',
        borderLeft: `3px solid ${getElementColor(card.element)}`,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '0 5px 5px 0',
        fontSize: '14px',
        color: '#CCC'
      }}>
        "{flavorText}"
      </div>
    );
  };
  
  // Afficher les informations narratives si disponibles
  const renderNarrativeData = () => {
    if (!card.narrativeData) return null;
    
    // Extraction sécurisée des données narratives
    const origin = card.narrativeData.origin || '';
    const flavorContext = card.narrativeData.flavorContext || '';
    const fullName = card.narrativeData.fullName || '';
    const occupation = card.narrativeData.occupation || '';
    
    return (
      <div style={{ 
        marginTop: '20px',
        width: '100%',
        borderTop: '1px dashed rgba(255, 255, 255, 0.3)',
        paddingTop: '15px'
      }}>
        <details>
          <summary style={{ 
            cursor: 'pointer',
            color: getElementColor(card.element),
            fontWeight: 'bold',
            marginBottom: '10px'
          }}>
            Informations additionnelles
          </summary>
          
          <div style={{ textAlign: 'left', fontSize: '14px' }}>
            {fullName && (
              <p><strong>Nom complet:</strong> {safeDisplay(fullName)}</p>
            )}
            
            {occupation && (
              <p><strong>Occupation:</strong> {safeDisplay(occupation)}</p>
            )}
            
            {origin && (
              <p><strong>Origine:</strong> {safeDisplay(origin)}</p>
            )}
            
            {flavorContext && (
              <div>
                <p><strong>Contexte:</strong></p>
                <p style={{ fontSize: '13px', color: '#BBB', lineHeight: '1.4' }}>
                  {safeDisplay(flavorContext)}
                </p>
              </div>
            )}
          </div>
        </details>
      </div>
    );
  };
  
  // Détermine l'élément à afficher pour l'image
  const elementForImage = getCardElement ? getCardElement(card) : (card.element ? card.element.toLowerCase() : 'fire');
  
  return (
    <div style={previewStyle} onClick={(e) => e.stopPropagation()}>
      <img 
        src={card.imageUrl || `/api/static/images/cards/${elementForImage}.svg`} 
        alt={safeDisplay(card.name) || 'Carte'} 
        style={cardImageStyle}
        onError={(e) => {
          e.target.src = `/api/static/images/card-backs/${elementForImage}-back.svg`;
        }}
      />
      
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '5px' }}>
        {card.rarity && (
          <span style={{ 
            color: getRarityColor(card.rarity),
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {safeDisplay(card.rarity).replace('_', ' ')}
          </span>
        )}
        
        {card.element && (
          <span style={{ 
            color: getElementColor(card.element),
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {safeDisplay(card.element)}
          </span>
        )}
      </div>
      
      <h3 style={{ 
        margin: '0 0 10px 0', 
        color: getElementColor(card.element),
        fontWeight: 'bold',
        fontSize: '20px',
        textAlign: 'center'
      }}>
        {safeDisplay(card.name) || 'Carte sans nom'}
      </h3>
      
      <div style={{ marginBottom: '10px', color: '#aaa' }}>
        {card.type && <span>Type: {safeDisplay(card.type)}</span>}
      </div>
      
      {getSpellSpeedInfo()}
      
      {card.attack !== undefined && card.defense !== undefined && (
        <div style={{ 
          marginBottom: '10px', 
          fontSize: '18px',
          display: 'flex',
          gap: '20px'
        }}>
          <span style={{ color: '#ff5a5a' }}>ATK: {safeDisplay(card.attack)}</span>
          {/* BUGFIX (2026-08-18) : PV réels (currentHP/maxHP) — avant : stat de base */}
          <span style={{ 
           color: (card.currentHP ?? card.maxHP ?? card.defense) < (card.maxHP ?? card.defense) ? '#ff3838' : '#5a5aff',
           fontWeight: (card.currentHP ?? card.maxHP ?? card.defense) < (card.maxHP ?? card.defense) ? 'bold' : 'normal'
          }}>
            PV: {safeDisplay(card.currentHP ?? card.defense)}
            {(card.maxHP && (card.maxHP !== (card.currentHP ?? card.maxHP))) ? 
              ` / ${safeDisplay(card.maxHP)}` : ''}
          </span>
        </div>
      )}
      
      {card.cost !== undefined && (
        <div style={{ 
          marginBottom: '15px',
          padding: '5px 15px',
          backgroundColor: 'rgba(90, 205, 255, 0.2)',
          borderRadius: '20px',
          border: '1px solid #5ACDFF'
        }}>
          <span style={{ color: '#5ACDFF', fontWeight: 'bold' }}>Coût: {safeDisplay(card.cost)} énergie</span>
        </div>
      )}
      
      {card.description && (
        <div style={{ 
          textAlign: 'left',
          lineHeight: '1.5',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          padding: '15px',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          width: '100%',
          marginBottom: '15px'
        }}>
          <p>{safeDisplay(card.description)}</p>
        </div>
      )}
      
      {renderEffects()}
      
      {renderFlavorText()}
      
      {renderNarrativeData()}
    </div>
  );
};

export default CardPreview;